export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: string;
  cover: string;
  tags: string[];
  heat: number;
  sections: ArticleSection[];
}

export interface Category {
  key: string;
  name: string;
  desc: string;
  icon: string;
}

export const categories: Category[] = [
  { key: "star", name: "明星动态", desc: "一手行程、片场花絮与公开活动记录", icon: "ri-user-star-line" },
  { key: "film", name: "影视综艺", desc: "剧集口碑、综艺亮点与幕后观察", icon: "ri-film-line" },
  { key: "music", name: "音乐现场", desc: "巡演、音乐节与舞台表现复盘", icon: "ri-music-2-line" },
  { key: "creator", name: "网红观察", desc: "内容创作者生态与平台热点追踪", icon: "ri-live-line" },
  { key: "fashion", name: "时尚生活", desc: "红毯造型、穿搭解析与生活方式", icon: "ri-t-shirt-line" },
  { key: "gossip", name: "八卦盘点", desc: "一周热梗回顾与话题脉络梳理", icon: "ri-chat-smile-2-line" },
];

export const articles: Article[] = [
  {
    slug: "summer-film-festival-red-carpet-review",
    title: "本届夏季电影节红毯复盘：造型关键词从「堆砌」转向「留白」",
    excerpt:
      "为期六天的夏季电影节落幕，红毯上最明显的变化不是谁穿得最贵，而是越来越多演员选择用剪裁和质感说话，繁复装饰明显退场。",
    category: "fashion",
    categoryName: "时尚生活",
    author: "林知遥",
    date: "2026-09-08",
    readTime: "7 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Artistic%20abstract%20editorial%20illustration%20of%20a%20red%20carpet%20walkway%20with%20warm%20amber%20and%20rose%20color%20blocks%2C%20geometric%20shapes%2C%20elegant%20minimal%20magazine%20cover%20style%2C%20soft%20cream%20background%2C%20no%20text&width=1200&height=800&seq=gw-hero-filmfest&orientation=landscape",
    tags: ["红毯", "造型解析", "电影节"],
    heat: 9860,
    sections: [
      {
        heading: "留白成为今年最贵的表达",
        paragraphs: [
          "如果把过去三年的红毯照片并排放在一起，今年最直观的感受是「减法」。层层叠叠的纱、夸张的立体结构、密集的亮片明显变少，取而代之的是干净的肩线、利落的腰线和一眼能看懂的廓形。",
          "造型师周迟在接受采访时提到，今年的沟通成本反而更高：「越简单越难做，因为没有装饰可以遮住剪裁上的问题。」一件纯色长裙要撑住镜头，面料垂坠和打版精准度都得过关。",
        ],
      },
      {
        heading: "三个被反复使用的颜色",
        paragraphs: [
          "统计了六天里近四十个公开亮相造型后，出现频率最高的三个颜色分别是奶油白、焦糖棕和深墨绿。它们共同的特点是饱和度偏低，配合场地暖光不容易过曝。",
          "奶油白之所以稳，是因为它在闪光灯下不会反射出突兀的高光；焦糖棕则更考验肤色的匹配度，稍微偏黄就会显得气色疲态。",
        ],
      },
      {
        heading: "配饰思路的变化",
        paragraphs: [
          "饰品从「叠加」回到「点睛」。往年动辄三层项链叠戴的画面几乎消失，今年更多是单件大尺寸耳饰，或者干脆只留一枚戒指。",
          "值得注意的是手包的体积明显缩小到装饰级别，实用性让位于整体比例——这一点在小个子演员身上尤其明显。",
        ],
      },
      {
        heading: "幕后工作流的升级",
        paragraphs: [
          "多家造型团队今年开始提前两周做「灯光测试」，把面料在场地同色温灯光下拍成样片再定稿。这个流程过去只出现在大型颁奖礼，如今成了常规操作。",
          "对普通观众来说，这些幕后细节最终呈现的就是一句话：今年的红毯看起来更安静，但也更耐看。",
        ],
      },
    ],
  },
  {
    slug: "new-variety-show-format-review",
    title: "新一季慢综艺开播：把「任务感」拿掉之后，节目反而好看了",
    excerpt:
      "取消强设定任务、弱化剪辑冲突，这档老牌慢综艺做了近几年最大的一次改版。第一期播完，观众讨论最多的居然是「终于不像在赶进度」。",
    category: "film",
    categoryName: "影视综艺",
    author: "陈砚秋",
    date: "2026-09-07",
    readTime: "6 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20editorial%20illustration%20of%20a%20cozy%20countryside%20house%20among%20olive%20green%20hills%20with%20amber%20sunlight%2C%20flat%20geometric%20shapes%2C%20cream%20paper%20texture%2C%20magazine%20cover%20style%2C%20no%20text&width=1000&height=750&seq=gw-film-variety&orientation=landscape",
    tags: ["综艺", "慢综艺", "改版"],
    heat: 7420,
    sections: [
      {
        heading: "从「完成挑战」到「过完一天」",
        paragraphs: [
          "改版最核心的一步是取消了贯穿全季的任务线。嘉宾不再需要在固定时间内完成某个目标，镜头更多地停留在做饭、闲聊、收拾院子的日常里。",
          "导演组在映后交流中解释，团队发现观众并不缺少刺激，缺的是「陪一段时间」的感觉。任务线一旦存在，所有对话都会不自觉围绕任务展开。",
        ],
      },
      {
        heading: "剪辑节奏明显放慢",
        paragraphs: [
          "第一期有效镜头数量比上一季同期下降了近三分之一，单个场景的平均时长从八秒拉长到二十秒以上。观众能完整看完一段对话，而不是被切碎成金句。",
          "这种剪辑对素材量要求反而更高，因为需要足够的连续镜头才能选出「什么都没发生但有味道」的段落。",
        ],
      },
      {
        heading: "观众反馈的两极",
        paragraphs: [
          "喜欢的人认为节目终于有了呼吸感；不习惯的观众则表示节奏太慢、记不住人名。这恰好说明改版确实动到了节目最核心的观看习惯。",
          "从弹幕词频看，「舒服」「下饭」两个词的出现频率比上一季翻了一倍多，这大概是对一档慢综艺最直接的肯定。",
        ],
      },
    ],
  },
  {
    slug: "music-festival-stage-production-notes",
    title: "音乐节舞台制作实录：为什么今年现场听起来更「稳」了",
    excerpt:
      "从返送系统到舞台监听的布局调整，今年的音乐节现场在人声清晰度上有明显提升。我们找了三位现场调音师聊了聊背后的改动。",
    category: "music",
    categoryName: "音乐现场",
    author: "郭牧",
    date: "2026-09-06",
    readTime: "8 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20concert%20stage%20with%20warm%20amber%20stage%20lights%20and%20rose%20glow%2C%20geometric%20silhouette%20crowd%2C%20minimal%20magazine%20poster%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-music-stage&orientation=landscape",
    tags: ["音乐节", "现场", "音响"],
    heat: 6180,
    sections: [
      {
        heading: "返送系统的重新分区",
        paragraphs: [
          "过去舞台返送往往是统一一路信号，乐手各自凭经验调整。今年多个舞台改成了按区域分路，鼓手、贝斯、主唱拿到的混音完全不同。",
          "这个改动带来的是更稳定的节奏——乐手不需要靠猜来判断自己的位置，失误率随之下降。",
        ],
      },
      {
        heading: "人声频段的主动避让",
        paragraphs: [
          "调音师普遍会把两千到四千赫兹之间让出一段空间给人声，把吉他中频适当压低。听感上人声会显得更靠前，但整体音量并不需要开得更大。",
          "难点在于平衡现场感与清晰度，压得太干净会失去乐队的粗粝质感，这也是不同舞台听感差异的主要来源。",
        ],
      },
      {
        heading: "给普通观众的实用建议",
        paragraphs: [
          "如果想要更好的听感，站位尽量避开正对舞台正中央的区域，那里往往是两套主扩声的叠加区，容易产生梳状滤波。",
          "靠近调音台后方二十米左右的位置，通常是整场听感最均衡的区域，也是调音师自己调试时参考的位置。",
        ],
      },
    ],
  },
  {
    slug: "short-video-creator-trend-shift",
    title: "短视频创作者转向长内容：一条十五分钟的片子正在变得划算",
    excerpt:
      "过去一年，多个百万粉创作者开始把主阵地从几十秒短视频转向十分钟以上的中长视频。流量逻辑变了，创作方式也跟着变。",
    category: "creator",
    categoryName: "网红观察",
    author: "苏迟",
    date: "2026-09-05",
    readTime: "6 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20flat%20editorial%20illustration%20of%20a%20camera%20and%20film%20frames%20arranged%20in%20a%20grid%2C%20amber%20and%20olive%20green%20color%20blocks%2C%20minimal%20knowledge%20base%20style%2C%20cream%20paper%20background%2C%20no%20text&width=1000&height=750&seq=gw-creator-trend&orientation=landscape",
    tags: ["创作者", "短视频", "内容趋势"],
    heat: 5720,
    sections: [
      {
        heading: "完播率之外的新指标",
        paragraphs: [
          "平台开始更看重「深度互动」，也就是收藏、长评论和二次打开。这些指标天然偏爱信息密度更高的中长内容。",
          "一位美食创作者告诉我们，同样题材做成一分钟和做成十二分钟，后者带来的收藏量是前者的六倍以上。",
        ],
      },
      {
        heading: "选题方式的重构",
        paragraphs: [
          "做长内容意味着不能只有一个钩子。创作者开始把一条片子拆成「问题—尝试—结果—复盘」四个段落，每段都留一个小悬念。",
          "代价是前期准备时间翻倍。过去一天能拍三条，现在一条要拍三天，但单条的长期收益也更稳定。",
        ],
      },
      {
        heading: "对观众意味着什么",
        paragraphs: [
          "最直接的变化是信息含量提高。同样讲一个冷知识，长视频有空间交代背景、给出反例、展示验证过程，而不是只抛结论。",
          "当然也不是所有题材都适合拉长。强情绪、强反转的内容在短时长里依然更有效，创作者的判断力变得更重要。",
        ],
      },
    ],
  },
  {
    slug: "weekly-gossip-recap-week36",
    title: "一周八卦复盘：从一次采访口误，到整条话题链的形成",
    excerpt:
      "本周娱乐话题几乎都围绕同一件事发酵。我们把时间线拆开，看看一个口误如何在四十八小时内变成全网讨论。",
    category: "gossip",
    categoryName: "八卦盘点",
    author: "瓜网编辑部",
    date: "2026-09-05",
    readTime: "5 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20overlapping%20speech%20bubbles%20and%20timeline%20dots%20in%20amber%20rose%20and%20olive%20tones%2C%20flat%20geometric%20minimal%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-gossip-weekly&orientation=landscape",
    tags: ["一周盘点", "话题", "复盘"],
    heat: 8210,
    sections: [
      {
        heading: "时间线：四十八小时发生了什么",
        paragraphs: [
          "周一晚间的一段采访片段被截取传播，其中一句口误因为语境缺失产生了歧义。周二中午相关话题进入热搜，当晚当事人工作室发布了一条简短说明。",
          "周三开始，讨论重心从「这句话本身」转向「采访剪辑是否断章取义」，舆论方向出现明显转折。",
        ],
      },
      {
        heading: "为什么这次发酵这么快",
        paragraphs: [
          "关键在于原始素材足够短，任何人都能在十秒内形成自己的判断，参与门槛极低。加上当事人在同期有作品在播，关注度本就处于高位。",
          "另一个推手是二次创作，多个账号用同一段素材做了不同角度的解读，进一步扩大了触达范围。",
        ],
      },
      {
        heading: "复盘给出的三点提醒",
        paragraphs: [
          "第一，看到片段先找完整版本；第二，注意信息发布的时间差，早期结论往往会在二十四小时内被修正；第三，区分「当事人说了什么」和「别人认为他表达了什么」。",
          "娱乐话题的传播规律其实很稳定，变的只是速度。",
        ],
      },
    ],
  },
  {
    slug: "actor-career-transition-interview",
    title: "从偶像剧到现实题材：一位演员的三年转型记录",
    excerpt:
      "三年前他还在拍甜宠剧，如今连着两部现实题材作品拿到不错口碑。转型不是换赛道那么简单，中间有很长一段没人找他的时间。",
    category: "star",
    categoryName: "明星动态",
    author: "林知遥",
    date: "2026-09-04",
    readTime: "9 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20solitary%20figure%20silhouette%20against%20warm%20amber%20gradient%20with%20rose%20accent%20shapes%2C%20minimal%20magazine%20feature%20style%2C%20cream%20texture%2C%20no%20text&width=1000&height=750&seq=gw-star-transition&orientation=landscape",
    tags: ["演员", "转型", "专访"],
    heat: 9340,
    sections: [
      {
        heading: "主动推掉的第一部戏",
        paragraphs: [
          "转型的起点是一次拒绝。他推掉了一部片酬更高的同类角色，理由是「再演一次，观众就彻底记住这个类型了」。",
          "推掉之后是长达半年的空窗期，没有合适的剧本找上门，团队一度动摇。",
        ],
      },
      {
        heading: "用配角找回表演方法",
        paragraphs: [
          "他先接了两个戏份不多的配角，角色性格和以往完全不同。小角色反而给了他试错空间，因为没人会用男主的期待去审视他。",
          "在片场他习惯把每场戏的动机写在本子上，这个方法一直保留到现在。",
        ],
      },
      {
        heading: "转型真正难在哪",
        paragraphs: [
          "他认为是「说服别人改变对你的想象」。观众、制片方、甚至身边的工作人员都有一套既定印象，打破它需要作品一部一部累积。",
          "如今他依然会收到偶像剧邀约，但他说现在可以挑，因为「至少有了说不的底气」。",
        ],
      },
    ],
  },
  {
    slug: "reality-show-editing-behind-scenes",
    title: "恋综剪辑幕后：一条感情线是怎样被「剪」出来的",
    excerpt:
      "同一批素材，可以剪出完全不同的故事走向。我们邀请了两位综艺剪辑师，还原一条感情线从素材到成片的加工过程。",
    category: "film",
    categoryName: "影视综艺",
    author: "陈砚秋",
    date: "2026-09-03",
    readTime: "7 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20interlocking%20geometric%20blocks%20and%20timeline%20bars%20in%20amber%20rose%20and%20olive%20tones%2C%20flat%20minimal%20design%20poster%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-film-editing&orientation=landscape",
    tags: ["恋综", "剪辑", "幕后"],
    heat: 4990,
    sections: [
      {
        heading: "素材比例远超想象",
        paragraphs: [
          "一季节目的原始素材通常在几百小时量级，成片只用到其中不到百分之三。剪辑师要做的第一件事不是选素材，而是建立人物关系表。",
          "他们会给每位嘉宾做情绪标注，记录同一个人在不同场景下的反应差异，再据此判断哪些互动是稳定的、哪些是偶发。",
        ],
      },
      {
        heading: "「心动」是被什么放大的",
        paragraphs: [
          "最常见的手法是放大反应镜头：一个眼神、一次停顿、一个没说完的句子。这些镜头单独看平淡，但放在关键对话之后会产生强烈暗示。",
          "背景音乐同样重要，同一段对话配上不同配乐，观众感受到的情绪浓度可以差出一倍。",
        ],
      },
      {
        heading: "边界在哪里",
        paragraphs: [
          "两位剪辑师都提到一条底线：不能拼接出从未发生的对话。可以做时间上的压缩与顺序调整，但不能改变语言本身的内容。",
          "观众越来越熟悉这套语言，过度加工反而容易被识破，所以近两年的趋势是节奏加快但加工痕迹变淡。",
        ],
      },
    ],
  },
  {
    slug: "idol-group-comeback-stage-analysis",
    title: "回归舞台解析：这一次编舞终于给主唱留了位置",
    excerpt:
      "某组合本周回归，舞台上线后讨论度很高。抛开粉丝滤镜，这次舞台在编舞结构和镜头分配上确实做了明显调整。",
    category: "music",
    categoryName: "音乐现场",
    author: "郭牧",
    date: "2026-09-02",
    readTime: "6 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20dancers%20as%20geometric%20silhouettes%20on%20a%20stage%20with%20amber%20spotlight%20and%20rose%20glow%2C%20minimal%20poster%20style%2C%20cream%20texture%2C%20no%20text&width=1000&height=750&seq=gw-music-comeback&orientation=landscape",
    tags: ["回归舞台", "编舞", "舞台"],
    heat: 6850,
    sections: [
      {
        heading: "编舞为声线让路",
        paragraphs: [
          "副歌之前的桥段明显降低了舞步密度，主唱站在舞台中前区完成高音，其余成员用队形托住画面。这种处理在以往版本里很少见。",
          "舞台导演的思路很清楚：先把听众的注意力交给人声，再在副歌用整齐的齐舞把情绪推上去。",
        ],
      },
      {
        heading: "镜头分配的调整",
        paragraphs: [
          "三分二十秒的舞台里，单人特写分散在五名成员身上，没有出现长时间锁定同一个人的情况。运镜从炫技式的快速推拉，变成了更克制的平移。",
          "对团体舞台来说，这种均衡分配能降低成员之间的争议，代价是单点记忆度下降。",
        ],
      },
      {
        heading: "一个值得注意的细节",
        paragraphs: [
          "舞蹈中断处的呼吸声被刻意保留，没有做降噪处理。这个小选择让舞台多了一点现场的粗粝感。",
          "整体来看，这次的制作思路是「做减法」，把资源集中在最容易被记住的三个瞬间。",
        ],
      },
    ],
  },
  {
    slug: "city-walk-lifestyle-trend",
    title: "City Walk 之后：周末城市漫游为什么开始讲「路线设计」",
    excerpt:
      "从随便走走，到提前做路线功课，城市漫游正在变成一种有门槛的周末方式。我们跟了三条被反复推荐的路线，看看它们的共同点。",
    category: "fashion",
    categoryName: "时尚生活",
    author: "苏迟",
    date: "2026-09-01",
    readTime: "5 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20city%20map%20with%20amber%20route%20line%20and%20olive%20green%20blocks%20representing%20parks%2C%20minimal%20design%20style%2C%20cream%20paper%2C%20no%20text&width=1000&height=750&seq=gw-fashion-citywalk&orientation=landscape",
    tags: ["城市漫步", "周末", "生活方式"],
    heat: 4310,
    sections: [
      {
        heading: "三条路线的共同点",
        paragraphs: [
          "我们选的三条路线长度集中在四到六公里，走完全程约一个半小时。这个距离刚好在「有点累」和「还想再走」之间。",
          "它们都经过至少两种不同的城市界面，比如老街区加河岸，或者商业区加公园，视觉变化本身就构成了节奏。",
        ],
      },
      {
        heading: "为什么开始需要设计",
        paragraphs: [
          "当一座城市被走得多了，随机路线的体验会迅速同质化。提前规划能让漫游避开重复的商铺界面，把时间花在真正有差异的空间上。",
          "设计并不是把每一分钟排满，而是确定几个必须经过的节点，其余部分留白。",
        ],
      },
      {
        heading: "实用建议",
        paragraphs: [
          "尽量把路线安排成单向，避免走回头路；每隔一公里左右安排一个可以坐下休息的节点；出发时间选在上午九点前后，人少且光线柔和。",
        ],
      },
    ],
  },
  {
    slug: "film-box-office-weekly-analysis",
    title: "本周票房观察：中小成本影片为什么能在周末逆袭",
    excerpt:
      "没有大导演、没有顶流阵容，一部成本不高的剧情片却在本周末跑出了不错的场均人次。它的口碑扩散路径值得研究。",
    category: "film",
    categoryName: "影视综艺",
    author: "陈砚秋",
    date: "2026-08-31",
    readTime: "6 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20rising%20bar%20chart%20in%20amber%20and%20olive%20tones%20with%20rose%20accent%2C%20flat%20minimal%20business%20magazine%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-film-boxoffice&orientation=landscape",
    tags: ["票房", "口碑", "市场"],
    heat: 3870,
    sections: [
      {
        heading: "首日排片并不理想",
        paragraphs: [
          "上映首日排片占比不到百分之八，多数场次在非黄金时段。但从周五晚场开始，场均人次明显高于同档期影片。",
          "院线反应很快，周六排片迅速上调，形成了典型的「口碑倒推排片」路径。",
        ],
      },
      {
        heading: "口碑扩散的两个节点",
        paragraphs: [
          "第一个节点是首映后的社交平台短评，多位非电影领域的内容创作者给出了具体到情节的推荐，可信度高于通稿式宣传。",
          "第二个节点是周末亲子与家庭观众的加入，影片在中后段的情感落点恰好击中了这部分人群。",
        ],
      },
      {
        heading: "对行业的一点提示",
        paragraphs: [
          "中小成本影片的窗口期很短，能否在首个周末形成自发传播，几乎决定了后续走势。与其铺量宣传，不如把资源集中在少数真正看过片的人身上。",
        ],
      },
    ],
  },
  {
    slug: "live-commerce-streamer-observation",
    title: "直播间里的新分工：为什么大主播开始带「选品团队」出镜",
    excerpt:
      "越来越多直播间把选品、打样、质检的幕后团队请到镜头前。这既是内容创新，也是一次信任重建。",
    category: "creator",
    categoryName: "网红观察",
    author: "苏迟",
    date: "2026-08-30",
    readTime: "5 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20lighting%20ring%20and%20sample%20boxes%20in%20amber%20and%20olive%20tones%2C%20flat%20geometric%20minimal%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-creator-livestream&orientation=landscape",
    tags: ["直播", "选品", "内容观察"],
    heat: 3620,
    sections: [
      {
        heading: "把流程搬上镜头",
        paragraphs: [
          "过去选品是直播之外的工作，观众只看结果。现在不少直播间会插入三五分钟的选品片段，现场演示对比、拆解原料。",
          "这段内容的转化率不一定最高，但它承担了另一种功能：让观众理解价格背后的取舍逻辑。",
        ],
      },
      {
        heading: "信任成本的变化",
        paragraphs: [
          "当观众能看见一件商品是怎么被筛掉的，对最终上架的品会更有信心。这比反复强调「全网最低价」更有效。",
          "对主播来说，这也提高了内容制作的门槛，需要团队具备真实可展示的流程。",
        ],
      },
    ],
  },
  {
    slug: "gossip-misinformation-checklist",
    title: "吃瓜防踩坑手册：看到一条爆料，先核对这四个信息",
    excerpt:
      "娱乐传闻每天都在发生，但大部分经不起四个基本问题的核对。这份清单不长，却能过滤掉绝大多数假消息。",
    category: "gossip",
    categoryName: "八卦盘点",
    author: "瓜网编辑部",
    date: "2026-08-29",
    readTime: "4 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20checklist%20card%20with%20magnifying%20glass%2C%20amber%20and%20rose%20accents%2C%20flat%20minimal%20design%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-gossip-checklist&orientation=landscape",
    tags: ["辟谣", "实用", "指南"],
    heat: 5240,
    sections: [
      {
        heading: "第一问：原始出处在哪里",
        paragraphs: [
          "绝大多数传闻在转述过程中会丢失出处。如果一条消息的所有版本都指向「有网友称」，那它基本不具备核实价值。",
          "追到最初的发布账号，看看它过往发过什么，通常一分钟内就能判断可信度。",
        ],
      },
      {
        heading: "第二问：时间对不对得上",
        paragraphs: [
          "把爆料里的事件和当事人公开行程放在一起比对。如果当天他明确在其他城市有公开活动，那这条消息至少存在重大疑点。",
        ],
      },
      {
        heading: "第三问：谁从传播中获益",
        paragraphs: [
          "留意消息的传播节点是否与作品宣传期、商业合作期重合。很多时候传播动机比内容本身更能说明问题。",
        ],
      },
      {
        heading: "第四问：有没有可验证的物料",
        paragraphs: [
          "文字描述可以被编造，但时间、地点、公开影像三者能互相印证时，可信度才会显著上升。缺一项，就建议先标注为「待证实」。",
        ],
      },
    ],
  },
  {
    slug: "vocalist-live-technique-notes",
    title: "现场演唱技巧笔记：歌手如何在高强度巡演中保护嗓子",
    excerpt:
      "密集巡演对声带是巨大考验。多位歌手在采访中分享了他们的开嗓流程、饮食禁忌和场间恢复方法，其中不少做法出奇地朴素。",
    category: "music",
    categoryName: "音乐现场",
    author: "郭牧",
    date: "2026-08-28",
    readTime: "7 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20sound%20waves%20and%20concentric%20circles%20in%20amber%20and%20olive%20tones%2C%20flat%20minimal%20poster%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-music-vocal&orientation=landscape",
    tags: ["演唱", "巡演", "声乐"],
    heat: 2980,
    sections: [
      {
        heading: "开嗓流程其实很短",
        paragraphs: [
          "多数歌手的热声时间在十五到二十分钟之间，以半闭合音和滑音为主，不会长时间大音量练习。过度热声反而会让声带在正式演出前就疲劳。",
        ],
      },
      {
        heading: "饮食上的普遍共识",
        paragraphs: [
          "演出前两小时不进食是共同习惯，冰饮、高糖饮品在巡演期间基本被排除。咖啡因的摄入会被控制在开场前六小时以上。",
          "环境湿度比很多人想象中重要，干燥空气是声带的隐形敌人。",
        ],
      },
      {
        heading: "场间恢复怎么做",
        paragraphs: [
          "连续两场演出之间的休息日通常会安排完全的声带静默，也就是几乎不说话。这个方法听起来极端，但对恢复效率提升明显。",
        ],
      },
    ],
  },
  {
    slug: "period-drama-production-design",
    title: "年代剧美术设计：如何用一件道具说服观众「这就是那个年代」",
    excerpt:
      "年代剧的真实感靠的不是口号，而是细节密度。美术指导分享了他们在道具、色彩和空间陈设上的取舍标准。",
    category: "film",
    categoryName: "影视综艺",
    author: "陈砚秋",
    date: "2026-08-27",
    readTime: "8 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20room%20with%20neatly%20arranged%20geometric%20objects%20in%20amber%20and%20olive%20tones%2C%20flat%20minimal%20interior%20poster%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-film-production&orientation=landscape",
    tags: ["年代剧", "美术", "幕后"],
    heat: 2450,
    sections: [
      {
        heading: "先定色彩，再定道具",
        paragraphs: [
          "美术组的顺序通常是先确定整体色彩基调，再根据色调挑选道具。这样才能避免单件道具好看但放进画面后不协调的问题。",
          "他们会把关键场景的墙面颜色做成色卡，贴在剧组每天都能看到的位置，全组保持同一套色彩记忆。",
        ],
      },
      {
        heading: "道具的「使用痕迹」",
        paragraphs: [
          "全新道具最出戏。团队会给家具、器皿做旧处理，甚至刻意留下一处不明显的磕碰。这种细节观众未必注意，但会形成整体可信度。",
        ],
      },
      {
        heading: "空间陈设反映人物关系",
        paragraphs: [
          "同一个客厅，在家庭关系紧张时和缓和后，摆设位置会有细微变化。这些变化不会在台词里说明，但镜头扫过时能被感受到。",
        ],
      },
    ],
  },
  {
    slug: "fan-culture-community-observation",
    title: "应援文化观察：从打投到公益，粉丝社群正在重新定义组织方式",
    excerpt:
      "越来越多粉丝社群把精力从打投转向公益与内容产出。这种转变背后，是社群对自身长期价值的一次重新思考。",
    category: "star",
    categoryName: "明星动态",
    author: "林知遥",
    date: "2026-08-26",
    readTime: "6 分钟",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20interconnected%20dots%20forming%20a%20network%20in%20amber%20rose%20and%20olive%20tones%2C%20flat%20minimal%20design%20style%2C%20cream%20background%2C%20no%20text&width=1000&height=750&seq=gw-star-fandoms&orientation=landscape",
    tags: ["粉丝社群", "应援", "观察"],
    heat: 3120,
    sections: [
      {
        heading: "组织方式的变化",
        paragraphs: [
          "过去的应援以短期、强执行为主，靠时间段内的集体行动形成声量。现在不少社群转向常态化运营，设立了内容组、翻译组、公益组。",
          "这种结构更稳定，但需要更明确的分工和更长的耐心。",
        ],
      },
      {
        heading: "公益成为黏合剂",
        paragraphs: [
          "公益项目的意义不仅是形象加分，它还为社群提供了工作内容，让成员在非作品周期也能保持连接。",
          "多个社群的负责人提到，公益项目带来的参与度甚至高于部分应援活动。",
        ],
      },
      {
        heading: "争议仍然存在",
        paragraphs: [
          "如何界定公益项目的对外表述、如何避免组织化带来的内部矛盾，仍是各个社群持续面对的课题。",
          "可以确定的是，粉丝社群正在从「围绕单次事件的组织」走向「有长期目标的内容共同体」。",
        ],
      },
    ],
  },
];

export const trendingKeywords: string[] = [
  "电影节红毯",
  "慢综艺改版",
  "音乐节音响",
  "创作者转型",
  "一周八卦",
  "票房逆袭",
  "现场演唱技巧",
];

export const faqs: { q: string; a: string }[] = [
  {
    q: "瓜网的内容来源是什么？编辑标准是怎样的？",
    a: "瓜网的内容由编辑部原创整理，以及基于公开信息的话题复盘。我们坚持三个原则：可追溯（标明信息出处与时间）、可验证（区分已证实与待证实的表述）、不伤害（不对当事人做无根据的私人评判）。",
  },
  {
    q: "网站上的热门榜单是怎么计算的？",
    a: "热瓜榜综合阅读量、收藏量、分享量与讨论时长四项指标，按最近 72 小时的数据加权计算。榜单每两小时更新一次，所以同一话题的热度会随讨论自然回落。",
  },
  {
    q: "我可以向瓜网投稿或提供线索吗？",
    a: "可以。通过「联系我们」页面的表单提交即可，请在内容中说明信息的时间、地点和可核实的物料。我们会先做初步核实，再决定是否跟进报道。",
  },
  {
    q: "为什么有些话题瓜网不跟进？",
    a: "涉及个人隐私、未经证实的情感传闻、以及可能对当事人造成实际伤害的信息，我们通常只做方法论层面的提醒，不参与具体传播。",
  },
  {
    q: "订阅瓜网邮箱会收到什么内容？",
    a: "每周一封，包含当周值得看的三篇长文、一份话题复盘和下周值得关注的日程提醒。不推送广告，随时可退订。",
  },
];